import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { HeroSection } from "@/components/HeroSection";
import { ProblemReveal } from "@/components/ProblemReveal";
import { HowItWorks } from "@/components/HowItWorks";
import { SmartProfiles } from "@/components/SmartProfiles";
import { StudioTeaser } from "@/components/CustomizationStudio";
import { ProductShowcase } from "@/components/ProductShowcase";
import {
  NutritionTrust,
  Testimonials,
  FinalCTA,
  ContactSection,
  Footer,
} from "@/components/LowerSections";
import StudioPage from "@/pages/StudioPage";
import MyKitchenPage from "@/pages/MyKitchenPage";
import CartPage from "@/pages/CartPage";
import AboutPage from "@/pages/AboutPage";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  return children;
}

function HomePage() {
  useEffect(() => {
    let raf;
    let lenis;
    const initLenis = async () => {
      const isCoarsePointer = window.matchMedia("(pointer: coarse)").matches;
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (isCoarsePointer || prefersReducedMotion) return;
      try {
        const Lenis = (await import("@studio-freight/lenis")).default;
        lenis = new Lenis({
          duration: 1.2,
          easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
          smoothWheel: true,
        });
        const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
        raf = requestAnimationFrame(loop);
      } catch (e) {
        console.warn("Lenis not available, using native scroll");
      }
    };
    initLenis();
    return () => { if (raf) cancelAnimationFrame(raf); if (lenis) lenis.destroy(); };
  }, []);

  return (
    <>
      <Navbar />
      <main>
        <HeroSection />
        <ProblemReveal />
        <HowItWorks />
        <SmartProfiles />
        <StudioTeaser />
        <ProductShowcase />
        <NutritionTrust />
        <Testimonials />
        <FinalCTA />
        <ContactSection />
      </main>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="bg-cream min-h-screen">
      <div className="grain-overlay" />
      <BrowserRouter>
        <AuthProvider>
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/studio" element={<StudioPage />} />
            <Route path="/my-kitchen" element={<ProtectedRoute><MyKitchenPage /></ProtectedRoute>} />
            <Route path="/cart" element={<ProtectedRoute><CartPage /></ProtectedRoute>} />
            <Route path="/about" element={<AboutPage />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
